import { useState } from 'react';
import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import { postGenerateLogoPrompt, type GenerateLogoPromptInput, type LogoPrompt } from '../api/generateLogoPrompt';
import { Page, Control, Content, type SelectorOption } from '../Components';
import { useGenerateLogoPromptParams } from '../Components/InspirationGenerate/useGenerateLogoPromptParams';
import { useCancelableEffect } from '../tools';

const LOADING_CONTAINER_STYLE = {
    textAlign: 'center' as const,
    padding: '20px',
};

const LOADING_TEXT_STYLE = {
    marginTop: '10px',
};

enum GENERATE_LOGO_PROMPT_CULTURE {
    EnUs = 'en-us',
    FrFr = 'fr-fr',
    FrBe = 'fr-be',
    DeDe = 'de-de',
    DeAt = 'de-at',
    EsEs = 'es-es',
    EsUs = 'es-us',
    EnCa = 'en-ca',
    EnIn = 'en-in',
    EnSg = 'en-sg',
    EnAu = 'en-au',
    EnNz = 'en-nz',
    EnGb = 'en-gb',
    EnIe = 'en-ie',
    ItIt = 'it-it',
    ItCh = 'it-ch',
    NlNl = 'nl-nl',
    NlBe = 'nl-be',
}
const CULTURE_VALUE_OPTIONS = Object.values(GENERATE_LOGO_PROMPT_CULTURE).map((culture) => {
    return { label: culture, value: culture };
});
export const GenerateLogoPromptPage = () => {
    const {
        shortDescription,
        tagLine,
        businessName,
        industry,
        businessDescription,
        maxGenerations,
        culture,
        setShortDescription,
        setTagLine,
        setBusinessName,
        setIndustry,
        setBusinessDescription,
        setMaxGenerations,
        setCulture,
    } = useGenerateLogoPromptParams();
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [prompts, setPrompts] = useState<LogoPrompt[]>([]);

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (callGenerate) {
                    setIsLoading(true);

                    const params: GenerateLogoPromptInput = {
                        businessName: businessName ?? '',
                    };

                    if (tagLine) params.tagLine = tagLine;
                    if (industry) params.industry = industry;
                    if (businessDescription) params.businessDescription = businessDescription;
                    if (maxGenerations) params.maxGenerations = maxGenerations;
                    if (shortDescription) params.shortDescription = shortDescription;
                    if (culture) params.culture = culture.value;
                    try {
                        const resultPrompts = await postGenerateLogoPrompt(params, signal);
                        setPrompts(resultPrompts.prompts);
                        setError(null); // Clear any previous error state
                    } catch (e) {
                        setError(e instanceof Error ? e.message : 'Failed to generate prompts');
                    } finally {
                        setIsLoading(false);
                        setCallGenerate(false);
                    }
                }
            })();
        },
        [callGenerate, shortDescription, tagLine, businessName, industry, businessDescription, maxGenerations, culture],
    );
    const onChangeSelectCulture = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setCulture(selected);
    };
    function onClickGenerate() {
        setCallGenerate(true);
    }
    return (
        <Page>
            <Page.Title>Generate Logo Prompt</Page.Title>
            <Page.Controls>
                <Control spacing="standard">
                    <Control.Title>Business Name</Control.Title>
                    <TextField
                        label="Business Name"
                        onChange={(e) => setBusinessName(e.target.value)}
                        value={businessName ?? ''}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Tagline</Control.Title>
                    <TextField label="Tagline" onChange={(e) => setTagLine(e.target.value)} value={tagLine} />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Industry</Control.Title>
                    <TextField label="Industry" onChange={(e) => setIndustry(e.target.value)} value={industry} />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Short Description</Control.Title>
                    <TextField
                        label="Short Description"
                        onChange={(e) => setShortDescription(e.target.value)}
                        value={shortDescription}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Business Description</Control.Title>
                    <TextField
                        label="Business Description"
                        onChange={(e) => setBusinessDescription(e.target.value)}
                        value={businessDescription}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Max Generations</Control.Title>
                    <TextField
                        label="Max Generations"
                        type="number"
                        min={1}
                        onChange={(e) => setMaxGenerations(parseInt(e.target.value))}
                        value={maxGenerations}
                    />
                </Control>
                <Control>
                    <Control.Title>Culture</Control.Title>
                    <Select
                        onChange={onChangeSelectCulture}
                        value={culture}
                        options={CULTURE_VALUE_OPTIONS}
                        label="Culture"
                    />
                </Control>
                <Control spacing="standard">
                    <Button onClick={onClickGenerate} disabled={!maxGenerations || callGenerate}>
                        Generate Prompt
                    </Button>
                </Control>
            </Page.Controls>
            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <div style={LOADING_CONTAINER_STYLE}>
                                <Spinner />
                                <p style={LOADING_TEXT_STYLE}>Generating logo prompt...</p>
                            </div>
                        </Content.Section>
                    </Content.Card>
                ) : error ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <div style={{ color: 'red', padding: '20px' }}>{error}</div>
                        </Content.Section>
                    </Content.Card>
                ) : prompts.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
                        {prompts.map((prompt, idx) => {
                            // Build query params dynamically
                            const params = new URLSearchParams();
                            params.set('prompt', prompt.prompt);
                            if (businessName) params.set('businessName', businessName);
                            if (businessDescription) params.set('businessDescription', businessDescription);
                            if (industry) params.set('industry', industry);
                            if (tagLine) params.set('tagLine', tagLine);
                            return (
                                <Content.Card key={idx} sticky size="large">
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8,
                                            textAlign: 'left',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <div>
                                            <strong>Title: </strong>
                                            {prompt.title}
                                        </div>
                                        <div>
                                            <strong>Prompt: </strong>
                                            {prompt.prompt}
                                        </div>
                                        <div>
                                            <strong>Significance: </strong>
                                            {prompt.significance}
                                        </div>
                                        <div>
                                            {/* Link to GenerateLogoPage with all relevant query params */}
                                            <a
                                                href={`/generate-logo?${params.toString()}`}
                                                style={{ color: '#0078d4', textDecoration: 'underline', marginTop: 8 }}
                                                target="_blank"
                                            >
                                                Use this prompt in Logo Generation
                                            </a>
                                        </div>
                                    </div>
                                </Content.Card>
                            );
                        })}
                    </div>
                ) : (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <div style={{ padding: '20px' }}>
                                This is a page to test the Generate logo prompt endpoint. Fill in the details and click
                                Generate Prompt to get logo prompts.
                            </div>
                        </Content.Section>
                    </Content.Card>
                )}
            </Page.Content>
        </Page>
    );
};

export default GenerateLogoPromptPage;
